import React, { useMemo, useState } from "react";
import { LuCrown } from "react-icons/lu";

const PIECES = {
  1: {
    name: "Red",
    rim: "rgba(2,6,8,0.98)",
    glow: "rgba(69,10,10,0.48)",
    background:
      "linear-gradient(145deg, #7f1010 0%, #4a0707 48%, #180202 100%)",
  },
  2: {
    name: "Blue",
    rim: "rgba(2,6,8,0.98)",
    glow: "rgba(15,36,68,0.42)",
    background:
      "linear-gradient(145deg, #2f5f93 0%, #173354 52%, #07111f 100%)",
  },
  3: {
    name: "Red king",
    king: true,
    rim: "rgba(2,6,8,0.98)",
    crown: "#FBBF24",
    glow: "rgba(69,10,10,0.52)",
    background:
      "linear-gradient(145deg, #8f1212 0%, #520808 48%, #190202 100%)",
  },
  4: {
    name: "Blue king",
    king: true,
    rim: "rgba(2,6,8,0.98)",
    crown: "#FBBF24",
    glow: "rgba(15,36,68,0.46)",
    background:
      "linear-gradient(145deg, #356da4 0%, #17375f 52%, #07111f 100%)",
  },
};

function ownerOf(piece) {
  if (piece === 1 || piece === 3) return 1;
  if (piece === 2 || piece === 4) return 2;
  return null;
}

export default function CheckersBoard({
  board,
  legalMoves = [],
  myPiece,
  currentTurn,
  isGameOver,
  onMove,
  maxWidthStyle,
}) {
  const [selected, setSelected] = useState(null);
  const cells = useMemo(() => Array.from(board || "").map(Number), [board]);
  const movesFromSelected = useMemo(
    () => legalMoves.filter((move) => Number(move.from) === Number(selected)),
    [legalMoves, selected]
  );
  const selectable = useMemo(
    () => new Set(legalMoves.map((move) => Number(move.from))),
    [legalMoves]
  );
  const targets = useMemo(
    () => new Set(movesFromSelected.map((move) => Number(move.to))),
    [movesFromSelected]
  );

  const canPlay = !isGameOver && myPiece === currentTurn;

  const handleCell = (idx) => {
    if (!canPlay) return;
    if (targets.has(idx) && selected !== null) {
      onMove(selected, idx);
      setSelected(null);
      return;
    }
    if (selectable.has(idx) && ownerOf(cells[idx]) === myPiece) {
      setSelected(idx);
      return;
    }
    setSelected(null);
  };

  return (
    <div
      className="relative aspect-square shrink-0 overflow-hidden rounded-[30px] border border-black/70 bg-[linear-gradient(145deg,#111827,#030607_58%,#010203)] p-2.5 shadow-[0_30px_90px_rgba(0,0,0,0.68),inset_0_1px_0_rgba(255,255,255,0.075),inset_0_-18px_32px_rgba(0,0,0,0.42)] mx-auto"
      style={{
        width: maxWidthStyle || "min(88vw, 640px)",
        maxWidth: "100%",
        contain: "layout paint size",
      }}
    >
      <div className="pointer-events-none absolute inset-x-8 top-0 z-10 h-px bg-gradient-to-r from-transparent via-white/18 to-transparent" />
      <div className="pointer-events-none absolute inset-0 z-10 rounded-[30px] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.035),inset_0_16px_36px_rgba(255,255,255,0.035),inset_0_-22px_42px_rgba(0,0,0,0.48)]" />
      <div className="grid h-full grid-cols-8 overflow-hidden rounded-[21px] border border-black/70 bg-[#030607] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.035),0_12px_28px_rgba(0,0,0,0.45)]">
        {cells.map((piece, idx) => {
          const row = Math.floor(idx / 8);
          const col = idx % 8;
          const dark = (row + col) % 2 === 1;
          const isSelected = selected === idx;
          const isTarget = targets.has(idx);
          const canSelect = selectable.has(idx) && ownerOf(piece) === myPiece && canPlay;
          const meta = PIECES[piece];

          return (
            <button
              key={idx}
              type="button"
              onClick={() => handleCell(idx)}
              className={[
                "relative flex min-h-0 min-w-0 items-center justify-center overflow-hidden focus:outline-none",
                dark
                  ? "bg-[#1A2434]"
                  : "bg-[#CAD4E0]",
                canSelect ? "cursor-pointer" : "cursor-default",
                isSelected ? "ring-2 ring-[#C6A15B] ring-inset z-10 shadow-[inset_0_0_0_1px_rgba(251,191,36,0.28)]" : "",
              ].join(" ")}
              style={{ contain: "layout paint" }}
              aria-label={`Square ${idx}${meta ? `, ${meta.name}` : ""}`}
            >
              {isTarget ? (
                <span className="absolute h-[30%] w-[30%] rounded-full border border-[#C6A15B]/75 bg-[#C6A15B]/22 shadow-[0_0_18px_rgba(198,161,91,0.28),inset_0_1px_0_rgba(255,255,255,0.18)]" />
              ) : null}
              {meta ? (
                <span
                  className="relative grid h-[70%] w-[70%] place-items-center rounded-full border-[3px] shadow-[inset_0_2px_0_rgba(255,255,255,0.10),inset_0_-10px_18px_rgba(0,0,0,0.34),0_12px_24px_rgba(0,0,0,0.48)]"
                  style={{
                    background: meta.background,
                    borderColor: meta.rim,
                    boxShadow: `inset 0 4px 10px rgba(255,255,255,0.26), inset 0 -8px 16px rgba(0,0,0,0.22), 0 12px 22px rgba(0,0,0,0.42), 0 0 22px ${meta.glow}`,
                  }}
                >
                  {meta.king ? (
                    <LuCrown
                      className="pointer-events-none absolute drop-shadow"
                      size={16}
                      strokeWidth={2.5}
                      style={{ color: meta.crown }}
                    />
                  ) : null}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
